// RENDU UNIQUE d'une User Story — CCT-99.
//
// Le détail d'une US s'affiche à plusieurs endroits (volet du kanban, panneau de context du
// chat, dialogue d'équipe). Un seul composant : sinon chaque écran redéveloppe sa variante et
// elles divergent — c'est exactement ce qui est arrivé aux trois rendus de chat.
import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator, Image, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View,
} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import ActionLoader from './ActionLoader';

import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';
import {
  addUsComment, listMissionCharacters, listUsActivity,
  type BoardUserStory, type MissionCharacter, type UsActivity, type UsStatus,
} from '../api/userStories';
import { useT } from '../i18n/useT';
import type { MessageKey } from '../i18n';

/**
 * Status → catalogue KEY. ⚠️ NOT the translated label.
 *
 * This map is exported and read by three files (the board columns, the task screen, the pill
 * below). Built once at import time, before any hook has run, it would freeze whatever language
 * loaded first: `?lang=fr` would translate every screen while these statuses stayed English.
 * Each consumer resolves the key through its own `t`, on every render.
 */
export const US_LABEL: Record<UsStatus, MessageKey> = {
  BACKLOG: 'us.status.BACKLOG', ASSIGNED: 'us.status.ASSIGNED',
  IN_PROGRESS: 'us.status.IN_PROGRESS', BLOCKED: 'us.status.BLOCKED',
  TO_REVIEW: 'us.status.TO_REVIEW', REVIEWED: 'us.status.REVIEWED',
  VALIDATED: 'us.status.VALIDATED', REFUSED: 'us.status.REFUSED', READY: 'us.status.READY',
};

export const US_COLOR: Record<UsStatus, string> = {
  BACKLOG: '#42526E', ASSIGNED: '#0052CC', IN_PROGRESS: '#0EA5E9', BLOCKED: '#EF4444',
  TO_REVIEW: '#F59E0B', REVIEWED: '#6554C0', VALIDATED: '#00875A', REFUSED: '#B91C1C', READY: '#00875A',
};

/**
 * Status → Ionicons name — CCT-345.
 *
 * ⚠️ THE ICON SAYS WHICH STATUS, IT IS NOT DECORATION. Every status screen used to head its card
 * with the SAME flag, so the icon carried no information at all: the only way to know where a task
 * stood was to read the label. Each one now shows what the status MEANS — a raised hand for
 * blocked, an eye for under review, a check for validated — and colour alone stops being the
 * carrier, which also makes the state readable without distinguishing red from green.
 *
 * Exported next to the colours and the labels: three facets of one fact, in one table.
 */
export const US_ICON: Record<UsStatus, string> = {
  BACKLOG: 'file-tray-outline',        // set aside, nobody has taken it yet
  ASSIGNED: 'person-add-outline',      // handed to the player
  IN_PROGRESS: 'hammer-outline',       // being worked on
  BLOCKED: 'hand-left-outline',        // the player has raised their hand
  TO_REVIEW: 'eye-outline',            // waiting to be looked at
  REVIEWED: 'search-outline',          // the engine has read it
  VALIDATED: 'checkmark-circle',       // accepted — filled, it is a conclusion
  REFUSED: 'refresh-circle',           // back to work, NOT a failure: it comes back
  READY: 'checkmark-done-outline',
};

/** Abandoned is not a status of the engine's table: it is a flag carried alongside. */
export const ABANDONED_ICON = 'remove-circle-outline';

/**
 * Statuses the player HANDS OVER to the owner character — CCT-345.
 *
 * Setting one of these is addressed to somebody: "read my delivery", "I am stuck". So the app
 * opens the conversation, where the server has already written the player's own sentence and the
 * character answers underneath. Starting to work (IN_PROGRESS) addresses no one and opens nothing.
 *
 * ⚠️ ONE LIST, IMPORTED BY BOTH SCREENS. The task screen and the kanban both move statuses; two
 * inline copies would drift, and the board would stop opening a conversation the task screen still
 * opens — for the same gesture.
 */
export const US_HANDS_OVER: UsStatus[] = ['TO_REVIEW', 'BLOCKED', 'READY'];

/** Qui parle dans le journal : le personnage en son nom, ou le contrôleur qu'il incarne. */
const AUTHOR_LABEL: Record<string, string> = {
  character: '', controller: 'revue de livraison', player: '', owner: '',
};

/** En-tête de section repliable : le compteur reste visible même replié, sinon on ne sait
 *  pas s'il y a quelque chose derrière. */
const Section: React.FC<{ title: string; count: number; open: boolean; onToggle: () => void }> =
  ({ title, count, open, onToggle }) => {
    const { isDarkTheme } = useTheme();
    const colors = getThemeColors(isDarkTheme);
    return (
      <TouchableOpacity onPress={onToggle} activeOpacity={0.7} style={st.sectionHead}>
        <Ionicons name={open ? 'chevron-down' : 'chevron-forward'} size={14} color={colors.textSecondary} />
        <Text style={[st.section, { color: colors.textPrimary }]}>{title}</Text>
        <View style={[st.countBadge, { backgroundColor: colors.surfaceSecondary }]}>
          <Text style={[st.countText, { color: colors.textSecondary }]}>{count}</Text>
        </View>
      </TouchableOpacity>
    );
  };

/** Abandon : le joueur a RENONCÉ, la revue n'a rien tranché. Afficher « Refusée » lui
 *  imputerait un jugement qui n'a pas eu lieu. */
const ABANDONED_COLOR = '#78716C';

export const StatusPill: React.FC<{ status: UsStatus; small?: boolean; abandoned?: boolean }> =
  ({ status, small, abandoned }) => {
    const t = useT();
    const color = abandoned ? ABANDONED_COLOR : US_COLOR[status];
    const label = t(abandoned ? 'us.status.ABANDONED' : US_LABEL[status]);
    const icon = abandoned ? ABANDONED_ICON : US_ICON[status];
    return (
      // Tinted background + a border of the SAME hue: on the dark theme a 20 %-opacity fill alone
      // left the pill floating, and two neighbouring statuses read as one block.
      <View style={[st.pill, small && st.pillSmall,
                    { backgroundColor: color + '26', borderColor: color + '80' }]}>
        <Ionicons name={icon} size={small ? 10 : 12} color={color} />
        <Text style={[st.pillText, small && st.pillTextSmall, { color }]}>{label}</Text>
      </View>
    );
  };

export const CharacterAvatar: React.FC<{ url?: string | null; name?: string | null; size?: number }> =
  ({ url, name, size = 24 }) => {
    const ok = url && url.startsWith('http');
    const box = { width: size, height: size, borderRadius: size / 2 };
    return ok
      ? <Image source={{ uri: url as string }} style={box} resizeMode="cover" />
      : <View style={[box, st.avatarFallback]}>
          <Text style={[st.avatarText, { fontSize: size * 0.45 }]}>
            {(name || '?').slice(0, 1).toUpperCase()}
          </Text>
        </View>;
  };

export interface UsContent {
  objective: string;
  validation: string;
  steps: { n: number; action: string; detail: string }[];
  codeTemplateId?: number;
}

export interface UsDetailProps {
  us: BoardUserStory;
  /** Abandon de la tâche (CCT-100) : REFUSED définitif, effet authoré sur la mission. */
  onAbandon?: () => void;
  /** Ouvre la template SOLUTION en lecture seule ; absent = non offerte ou tâche en cours. */
  onOpenSolution?: () => void;
  content?: UsContent | null;
  /** Statuts que le joueur peut poser ; vide = pas d'actions proposées. */
  settable?: UsStatus[];
  onSetStatus?: (s: UsStatus) => void;
  onOpenProject?: (templateId: number) => void;
  onClose?: () => void;
  /** Le journal est coûteux (un appel par US) : on ne le charge que si l'écran le veut. */
  showActivity?: boolean;
}

/** COMMENTAIRES + JOURNAL d'une US, avec composeur et autocomplete `@`.
 *
 *  Exporté pour que l'écran de tâche l'affiche AUSSI : c'est le même objet métier, il ne doit
 *  pas exister deux implémentations (le chat a montré où mène la duplication).
 */
export const UsActivityPanel: React.FC<{
  missionId: number;
  elementId: string | number;
  /** Rechargé quand le statut change : le journal doit refléter la transition. */
  statusKey?: string;
  /** Sections repliées au départ : le détail d'une US doit tenir à l'écran, l'historique
   *  ne s'ouvre qu'à la demande. */
  defaultOpen?: boolean;
}> = ({ missionId, elementId, statusKey, defaultOpen = false }) => {
  const t = useT();
  const [openComments, setOpenComments] = React.useState(defaultOpen);
  const [openLog, setOpenLog] = React.useState(defaultOpen);
  const { getAccessToken } = useAuth();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);

  const [activity, setActivity] = useState<UsActivity[]>([]);
  const [loadingLog, setLoadingLog] = useState(false);
  const [draft, setDraft] = useState('');
  const [chars, setChars] = useState<MissionCharacter[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const loadLog = useCallback(async () => {
    setLoadingLog(true);
    try { setActivity((await listUsActivity(getAccessToken(), missionId, elementId)).activity ?? []); }
    catch { setActivity([]); }
    finally { setLoadingLog(false); }
  }, [missionId, elementId, statusKey, getAccessToken]);

  useEffect(() => { void loadLog(); }, [loadLog]);

  useEffect(() => {
    void (async () => {
      try { setChars((await listMissionCharacters(getAccessToken(), missionId)).characters ?? []); }
      catch { setChars([]); }
    })();
  }, [missionId, getAccessToken]);

  /** Le jeton de mention court du DERNIER `@` jusqu'au curseur : un nom de personnage
   *  contient une espace, découper au premier séparateur ne le retrouverait jamais. */
  const onDraft = (t: string) => {
    setDraft(t);
    const at = t.lastIndexOf('@');
    setMentionQuery(at < 0 ? null : t.slice(at + 1).toLowerCase());
  };

  const pickMention = (c: MissionCharacter) => {
    const at = draft.lastIndexOf('@');
    setDraft(`${draft.slice(0, at)}@${c.name} `);
    setMentionQuery(null);
  };

  // CCT-165 — l'import de storyline crée des personnages PLACEHOLDER tous nommés « Personnage »
  // pour les speakerColors non déclarés (cf. CCT-103, qui les écartait déjà côté émetteur de
  // notification). Les proposer en mention n'a aucun sens : quatre entrées identiques, dont
  // aucune ne désigne quelqu'un. On ne suggère que les personnages RÉELS.
  const GENERIC = ['personnage', 'character', 'npc', 'pnj', 'narrateur', 'narrator'];
  // On écarte les PLACEHOLDERS génériques ET le personnage INCARNÉ par le joueur (CCT-167) :
  // se mentionner soi-même n'a pas de sens, tout comme il ne figure pas dans sa propre équipe.
  const nommes = chars.filter(c =>
    !GENERIC.includes((c.name || '').trim().toLowerCase()) && !c.is_player);
  const suggestions = mentionQuery == null ? [] : nommes.filter(c =>
    (c.name || '').toLowerCase().startsWith(mentionQuery)).slice(0, 5);

  const send = async () => {
    if (!draft.trim() || sending) return;
    setSending(true);
    try {
      const res = await addUsComment(getAccessToken(), missionId, elementId, draft.trim());
      setActivity(res.activity ?? []);
      setDraft(''); setMentionQuery(null);
    } catch { /* le commentaire reste dans le champ : rien n'est perdu */ }
    finally { setSending(false); }
  };

  const when = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
  };

  const comments = activity.filter(a => a.kind === 'comment');
  const statuses = activity.filter(a => a.kind === 'status');

  return (
    <View style={{ gap: 8 }}>
      {/* Les COMMENTAIRES ne sont pas la discussion : ils restent attachés à la tâche et se
          relisent plus tard. Le journal des statuts vit juste en dessous. */}
      <Section title={t('us.comments')} count={comments.length}
        open={openComments} onToggle={() => setOpenComments(o => !o)} />
      {openComments && comments.length === 0 && !loadingLog && (
        <Text style={[st.dim, { color: colors.textTertiary }]}>{t('us.comments.empty')}</Text>
      )}
      {openComments && comments.map(a => (
        <View key={a.activity_id} style={st.logRow}>
          <CharacterAvatar url={a.avatarkey} name={a.character_name ?? 'Vous'} size={22} />
          <View style={{ flex: 1, gap: 3 }}>
            <Text style={[st.logAuthor, { color: colors.textSecondary }]}>
              {a.author === 'player' ? 'Vous' : (a.character_name || 'Personnage')}
              {AUTHOR_LABEL[a.author ?? ''] ? ` · ${AUTHOR_LABEL[a.author ?? '']}` : ''}
            </Text>
            <Text style={[st.text, { color: colors.textPrimary }]}>{a.body}</Text>
            <Text style={[st.logTime, { color: colors.textTertiary }]}>{when(a.created_at)}</Text>
          </View>
        </View>
      ))}

      {/* Autocomplete `@` : au-dessus du champ, pour ne pas masquer ce qu'on écrit. */}
      {openComments && suggestions.length > 0 && (
        <View style={[st.suggest, { backgroundColor: colors.surfaceSecondary, borderColor: colors.border }]}>
          {suggestions.map(c => (
            <TouchableOpacity key={c.characterid} onPress={() => pickMention(c)} style={st.suggestRow}>
              <CharacterAvatar url={c.avatarkey} name={c.name} size={20} />
              <Text numberOfLines={1} style={[st.text, { color: colors.textPrimary, flex: 1 }]}>{c.name}</Text>
              {!!c.role && (
                <Text numberOfLines={1} style={[st.dim, { color: colors.textTertiary, flex: 1 }]}>{c.role}</Text>
              )}
            </TouchableOpacity>
          ))}
        </View>
      )}

      {openComments && (
      <View style={[st.composer, { borderColor: colors.border, backgroundColor: colors.surfaceSecondary }]}>
        <TextInput
          value={draft}
          onChangeText={onDraft}
          placeholder={t('us.comments.placeholder')}
          placeholderTextColor={colors.textTertiary}
          multiline
          style={[st.input, { color: colors.textPrimary }]}
        />
        <TouchableOpacity onPress={send} disabled={sending || !draft.trim()}
          style={[st.send, { backgroundColor: colors.primary, opacity: sending || !draft.trim() ? 0.4 : 1 }]}>
          {sending
            ? <ActivityIndicator size="small" color={colors.badgeText} />
            : <Ionicons name="send" size={14} color={colors.badgeText} />}
        </TouchableOpacity>
      </View>
      )}

      <Section title={t('us.activity')} count={statuses.length}
        open={openLog} onToggle={() => setOpenLog(o => !o)} />
      {openLog && loadingLog && <ActionLoader label={t('us.activity.loading')} />}
      {openLog && !loadingLog && statuses.length === 0 && (
        <Text style={[st.dim, { color: colors.textTertiary }]}>{t('us.activity.empty')}</Text>
      )}
      {openLog && statuses.map(a => (
        <View key={a.activity_id} style={st.logRow}>
          <CharacterAvatar url={a.avatarkey}
            name={a.author === 'owner' ? a.character_name : 'Vous'} size={22} />
          <View style={{ flex: 1, gap: 3 }}>
            <View style={st.statusLine}>
              {!!a.from_status && <StatusPill status={a.from_status} small />}
              {!!a.from_status && <Ionicons name="arrow-forward" size={11} color={colors.textTertiary} />}
              {!!a.to_status && <StatusPill status={a.to_status} small />}
            </View>
            {/* QUI a changé le statut : le joueur, ou le personnage après revue. */}
            <Text style={[st.logTime, { color: colors.textTertiary }]}>
              {a.author === 'owner' ? (a.character_name || t('us.owner')) : t('us.author.you')} · {when(a.created_at)}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
};

export default function UsDetail({
  us, content, settable = [], onSetStatus, onOpenProject, onClose, showActivity = true,
  onAbandon, onOpenSolution,
}: UsDetailProps) {
  const t = useT();
  const { getAccessToken } = useAuth();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);


  return (
    <View style={st.root}>
      <View style={st.head}>
        <Text numberOfLines={3} style={[st.title, { color: colors.textPrimary }]}>
          {us.title || `US ${us.element_id}`}
        </Text>
        {!!onClose && (
          <TouchableOpacity onPress={onClose} style={st.close}>
            <Ionicons name="close" size={18} color={colors.textSecondary} />
          </TouchableOpacity>
        )}
      </View>

      <StatusPill status={us.status} abandoned={us.abandoned} />

      <View style={st.ownerRow}>
        <CharacterAvatar url={us.owner_avatarkey} name={us.owner_name} size={26} />
        <Text style={[st.row, { color: colors.textSecondary }]}>
          {us.owner_name || t('us.owner.none')}
        </Text>
      </View>
      <Text style={[st.row, { color: colors.textSecondary }]}>
        Mission : {us.mission_title ?? us.mission_id}
      </Text>
      <Text style={[st.row, { color: colors.textSecondary }]}>
        Contrôleur : {us.controller || 'aucun'}
      </Text>

      {settable.length > 0 && (
        <>
          <Text style={[st.section, { color: colors.textPrimary }]}>{t('us.action.changeStatus')}</Text>
          <View style={st.actions}>
            {(us.status === 'READY' ? [] : us.settable ?? settable).map(s => (
              <TouchableOpacity key={s} disabled={s === us.status} onPress={() => onSetStatus?.(s)}
                style={[st.action, { borderColor: US_COLOR[s], opacity: s === us.status ? 0.4 : 1 }]}>
                <Text style={[st.actionText, { color: colors.textPrimary }]}>{t(US_LABEL[s])}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </>
      )}

      {/* La SOLUTION ne s'affiche qu'une fois la tâche terminée — et c'est le SERVEUR qui
          tranche : ce bouton n'est qu'un raccourci, pas la garde. */}
      {!!onOpenSolution && (
        <TouchableOpacity onPress={onOpenSolution}
          style={[st.ide, { backgroundColor: US_COLOR.VALIDATED }]}>
          <Ionicons name="bulb-outline" size={14} color="#FFFFFF" />
          <Text style={[st.ideText, { color: '#FFFFFF' }]}>{t('us.action.seeSolution')}</Text>
        </TouchableOpacity>
      )}

      {/* Abandonner : action DESTRUCTRICE (statut définitif) — d'où le rouge et le retrait
          dès que la tâche est terminée. */}
      {!!onAbandon && us.status !== 'VALIDATED' && us.status !== 'REFUSED' && us.status !== 'READY' && (
        <TouchableOpacity onPress={onAbandon}
          style={[st.abandon, { borderColor: US_COLOR.REFUSED }]}>
          <Ionicons name="close-circle-outline" size={14} color={US_COLOR.REFUSED} />
          <Text style={[st.abandonText, { color: US_COLOR.REFUSED }]}>{t('us.action.abandon')}</Text>
        </TouchableOpacity>
      )}

      {!!content?.codeTemplateId && !!onOpenProject && (
        <TouchableOpacity onPress={() => onOpenProject(content.codeTemplateId!)}
          style={[st.ide, { backgroundColor: colors.primary }]}>
          <Ionicons name="code-slash-outline" size={14} color={colors.badgeText} />
          <Text style={[st.ideText, { color: colors.badgeText }]}>{t('us.action.openProject')}</Text>
        </TouchableOpacity>
      )}

      <ScrollView style={st.body} contentContainerStyle={{ gap: 8 }}>
        {content === null && (
          <Text style={[st.dim, { color: colors.textTertiary }]}>
            {t('us.contentMissing')}
          </Text>
        )}

        {!!content?.objective && (
          <>
            <Text style={[st.section, { color: colors.textPrimary }]}>{t('us.objective')}</Text>
            <Text style={[st.text, { color: colors.textSecondary }]}>{content.objective}</Text>
          </>
        )}
        {!!content?.steps?.length && (
          <>
            <Text style={[st.section, { color: colors.textPrimary }]}>{t('us.steps')}</Text>
            {content.steps.map(s => (
              <View key={s.n} style={st.step}>
                <Text style={[st.stepN, { color: colors.primary }]}>{s.n}.</Text>
                <View style={{ flex: 1 }}>
                  <Text style={[st.text, { color: colors.textPrimary }]}>{s.action}</Text>
                  {!!s.detail && <Text style={[st.dim, { color: colors.textTertiary }]}>{s.detail}</Text>}
                </View>
              </View>
            ))}
          </>
        )}
        {!!content?.validation && (
          <>
            <Text style={[st.section, { color: colors.textPrimary }]}>{t('us.validation')}</Text>
            <Text style={[st.text, { color: colors.textSecondary }]}>{content.validation}</Text>
          </>
        )}

        {showActivity && (
          <UsActivityPanel missionId={us.mission_id} elementId={us.element_id} statusKey={us.status} />
        )}
      </ScrollView>
    </View>
  );
}

const st = StyleSheet.create({
  root: { flex: 1, minHeight: 0, gap: 8 },
  head: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  title: { fontSize: 15, fontWeight: '700', flex: 1 },
  close: { padding: 2 },
  pill: { alignSelf: 'flex-start', borderRadius: 999, paddingHorizontal: 10, paddingVertical: 4,
          flexDirection: 'row', alignItems: 'center', gap: 5, borderWidth: 1 },
  pillSmall: { paddingHorizontal: 7, paddingVertical: 2, gap: 4 },
  pillText: { fontSize: 11, fontWeight: '700' },
  pillTextSmall: { fontSize: 10 },
  ownerRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  row: { fontSize: 12 },
  section: { fontSize: 13, fontWeight: '700' },
  sectionHead: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 10 },
  countBadge: { borderRadius: 999, paddingHorizontal: 7, paddingVertical: 1 },
  countText: { fontSize: 10, fontWeight: '700' },
  actions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  action: { borderWidth: 1.5, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 7 },
  actionText: { fontSize: 12, fontWeight: '600' },
  ide: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
         borderRadius: 10, paddingVertical: 9, marginTop: 6 },
  ideText: { fontSize: 13, fontWeight: '700' },
  abandon: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
             borderWidth: 1, borderRadius: 10, paddingVertical: 8, marginTop: 6 },
  abandonText: { fontSize: 12, fontWeight: '700' },
  body: { flex: 1, minHeight: 0, marginTop: 4 },
  text: { fontSize: 12, lineHeight: 17 },
  dim: { fontSize: 11, lineHeight: 16 },
  step: { flexDirection: 'row', gap: 6 },
  stepN: { fontSize: 12, fontWeight: '700' },
  avatarFallback: { backgroundColor: '#475569', alignItems: 'center', justifyContent: 'center' },
  avatarText: { color: '#FFFFFF', fontWeight: '700' },
  logRow: { flexDirection: 'row', gap: 8, alignItems: 'flex-start' },
  statusLine: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  logAuthor: { fontSize: 11, fontWeight: '600' },
  logTime: { fontSize: 10 },
  suggest: { borderWidth: 1, borderRadius: 10, overflow: 'hidden' },
  suggestRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 8, paddingVertical: 7 },
  composer: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, borderWidth: 1,
              borderRadius: 10, paddingHorizontal: 8, paddingVertical: 6, marginTop: 4 },
  input: { flex: 1, fontSize: 12, minHeight: 34, maxHeight: 90, outlineStyle: 'none' } as any,
  send: { width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center' },
});
